/**
 * The Members screen itself, addressed at `/pools?poolId=<id>&view=members`.
 * This pane is MEMBER-VISIBLE, not officer-only, and does not live in pool
 * settings: `poolSettingsState.ts` returns `noAccess` for a plain member,
 * which would make R1's `Members N` section and R6/D4's plain-member Invited
 * behavior unobservable. See `plan.md` `## 5.17`.
 *
 * `POST /pool/get`, `POST /member/list` and `POST /profile/get` are the
 * three reads that drive it: the header, the loading skeletons, the failed
 * retry banner, the shared no-access state, the empty body, and the three
 * ordered sections (Requests, Members, Invited).
 */
import {
  useInviteRevoke,
  useMemberApprove,
  useMemberList,
  useMemberReject,
  useMemberRemove,
  useMemberSetRole,
  useMemberSuspend,
  useMemberUnsuspend,
  usePoolGet,
  usePoolLeave,
} from "@sportsmet/pool-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { Button, confirmDestructive, InlineBanner, Input } from "@sportsmet/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  isPoolNoAccessCode,
  POOL_NO_ACCESS_ACTION_LABEL,
  POOL_NO_ACCESS_BODY,
  POOL_NO_ACCESS_TITLE,
} from "../../errors/poolAccess";
import { useOverlay } from "../../routeState/useOverlay";
import { ReportAccountDialog } from "../messageModeration/ReportAccountDialog";
import { useBlockFan } from "../messageModeration/useBlockFan";
import { useOpenPrivateChat } from "../messages/useOpenPrivateChat";
import {
  GIVE_AWARD_SHEET_NAME,
  GiveAwardSheet,
} from "../poolAwards/GiveAwardSheet";
import { InviteSheet } from "../poolInvite/InviteSheet";
import { INVITE_ENTRY_LABEL } from "../poolInvite/inviteSheetCopy";
import {
  INVITE_SHEET_NAME,
  isInviteSheetOpen,
  readInviteTab,
} from "../poolInvite/inviteSheetRoute";
import {
  REVIVE_SHEET_NAME,
  ReviveSheetContainer,
} from "../poolRevive/ReviveSheetContainer";
import {
  APPROVE_ALL_CONFIRM_LABEL,
  APPROVE_ALL_DIALOG_NAME,
  ApproveAllDialog,
} from "./ApproveAllDialog";
import {
  HAND_OVER_DIALOG_NAME,
  HandOverPoolDialog,
} from "./HandOverPoolDialog";
import { memberActionConfirm } from "./memberActionConfirm";
import { MemberActionsDialog } from "./MemberActionsDialog";
import {
  buildMemberOverflowMenu,
  type typeMemberMenuActionId,
} from "./memberOverflowMenu";
import { buildMembersRosterModel } from "./membersRosterModel";
import { PoolMemberInvitedRow } from "./PoolMemberInvitedRow";
import { PoolMemberRequestRow } from "./PoolMemberRequestRow";
import { PoolMemberRow, PoolMemberRowSkeleton } from "./PoolMemberRow";
import {
  MEMBER_ACTIONS_SHEET_NAME,
  readMemberActionsTarget,
  readReportMemberTarget,
  REPORT_MEMBER_SHEET_NAME,
} from "./poolMembersRoute";

/**
 * The member-menu ids this screen can act on. `revive`, `giveAward` and
 * `report` open their own sheets; `report` opens `ReportAccountDialog`
 * (ISSUE-1045). `message` opens the one-to-one chat in Messages through
 * `useOpenPrivateChat` (ISSUE-1045). `block` and `leavePool` reuse built
 * mutations, and ISSUE-817 added the five role and membership mutations.
 * `viewProfile` opens the pool-scoped member detail pane (ISSUE-194).
 *
 * Every id in `typeMemberMenuActionId` is now in this set, so no row in this
 * menu renders the not-ready note. Removing that dead gate from
 * `MemberActionsDialog` is a separate refactor.
 */
const READY_MEMBER_ACTION_IDS = new Set<typeMemberMenuActionId>([
  "viewProfile",
  "message",
  "revive",
  "giveAward",
  "block",
  "report",
  "leavePool",
  "makeAdmin",
  "makeMember",
  "suspend",
  "unsuspend",
  "removeFromPool",
]);

/**
 * The five ids this pane sends a member mutation for. Every one of them goes
 * through `memberActionConfirm` first, and `unsuspend` is the single id whose
 * confirm is legitimately `null`, restoring access destroys nothing.
 */
const MEMBER_MUTATION_ACTION_IDS = [
  "makeAdmin",
  "makeMember",
  "suspend",
  "unsuspend",
  "removeFromPool",
] as const;

type typeMemberMutationActionId = (typeof MEMBER_MUTATION_ACTION_IDS)[number];

function isMemberMutationActionId(
  id: typeMemberMenuActionId,
): id is typeMemberMutationActionId {
  return (MEMBER_MUTATION_ACTION_IDS as readonly string[]).includes(id);
}

export type typePoolMembersPaneProps = {
  poolId: string;
  searchQuery: string;
  onSearchQueryChange: (next: string) => void;
  /**
   * Where a back control on this pane goes, when it has one.
   *
   * Omitted when the pane is the pool screen's Members TAB, because a tab has
   * nothing behind it: `MEMBERS_BACK_LABEL` promises a trip back to the pool
   * and the only route out of a tab leaves the pool altogether, which is the
   * opposite of what the label says. The pool screen's own back arrow, one
   * row up, is the way out there.
   */
  onGoBack?: (() => void) | undefined;
  /** Opens one member's pool-scoped profile (ISSUE-194). */
  onOpenMemberDetail: (userId: string) => void;
};

export const MEMBERS_TITLE = "Members";
export const MEMBERS_BACK_LABEL = "Back to pool";
export const MEMBERS_LOADING_LABEL = "Loading members";
export const MEMBERS_FAILED_MESSAGE = "We could not load these members.";
export const MEMBERS_RETRY_LABEL = "Try again";
export const MEMBERS_EMPTY_BODY = "No one is in this pool yet.";
export const MEMBERS_SEARCH_LABEL = "Search members";
export const MEMBERS_SEARCH_MISS_BODY = "No members match that search.";
export const MEMBERS_CONTEST_UNAVAILABLE_MESSAGE =
  "Contest standings are unavailable right now.";

export function sectionHeading(title: string, count: number): string {
  return `${title} ${count}`;
}

export const PoolMembersPane = ({
  poolId,
  searchQuery,
  onSearchQueryChange,
  onGoBack,
  onOpenMemberDetail,
}: typePoolMembersPaneProps): ReactNode => {
  const pool = usePoolGet({ poolId });
  const members = useMemberList({
    poolId,
    statuses: ["active", "pending", "suspended"],
    limit: 200,
  });
  const viewer = useProfileGet({});
  const overlay = useOverlay();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const approve = useMemberApprove();
  const reject = useMemberReject();
  const revoke = useInviteRevoke();
  const leavePool = usePoolLeave();
  const setRole = useMemberSetRole();
  const suspend = useMemberSuspend();
  const unsuspend = useMemberUnsuspend();
  const removeMember = useMemberRemove();
  const { blockFan, isPending: isBlockPending } = useBlockFan();
  const { openPrivateChat, isPending: isOpeningChat } = useOpenPrivateChat();
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  // `useBlockFan` exposes no `variables`, so the acting member is recorded
  // here. Without it the shared pending flag would make ANOTHER member's Block
  // row look busy if the fan dismissed the dialog mid-flight and opened theirs.
  const [blockingUserId, setBlockingUserId] = useState<string | null>(null);
  // Same reason as `blockingUserId`: `useOpenPrivateChat` exposes no `variables`.
  const [openingChatUserId, setOpeningChatUserId] = useState<string | null>(
    null,
  );
  // The member mutation currently in flight, or null. It is BOTH the busy-row
  // marker and the re-entry guard: none of these five endpoints takes a request
  // id, so this flag is the whole defense against a second press sending the
  // mutation twice.
  const [pendingMemberActionId, setPendingMemberActionId] =
    useState<typeMemberMutationActionId | null>(null);

  const poolErrorCode = extractErrorCode(pool.data);
  const membersErrorCode = extractErrorCode(members.data);

  let phase: "noAccess" | "failed" | "loading" | "ready";
  if (isPoolNoAccessCode(poolErrorCode)) {
    phase = "noAccess";
  } else if (
    pool.isError ||
    members.isError ||
    poolErrorCode !== null ||
    membersErrorCode !== null
  ) {
    phase = "failed";
  } else if (pool.isLoading || members.isLoading || viewer.isLoading) {
    phase = "loading";
  } else if (pool.data?.data?.viewerPoolRole === undefined) {
    phase = "failed";
  } else if (viewer.data?.data?.profile?.accountId === undefined) {
    // WITHOUT THE VIEWER'S OWN ID THIS PANE CANNOT TELL WHOSE ROW IS WHOSE.
    // `buildMemberOverflowMenu` decides the own-row branch by comparing ids, so
    // an unknown viewer id silently turns the Commissioner's own row into
    // "an officer looking at the owner" and a plain member's own row into a
    // stranger's, no `Leave pool` on either, which is R4 broken. A failed
    // read is the honest answer. Found by the ISSUE-817 browser pass, where
    // `/profile/get` failed and the pane carried on regardless.
    phase = "failed";
  } else {
    phase = "ready";
  }

  const viewerPoolRole = pool.data?.data?.viewerPoolRole ?? "member";
  const canInvite =
    viewerPoolRole === "owner" ||
    viewerPoolRole === "admin" ||
    (pool.data?.data?.pool.memberSharingEnabled ?? false);
  const viewerUserId = viewer.data?.data?.profile?.accountId ?? "";

  const model = buildMembersRosterModel({
    members: members.data?.data?.items ?? [],
    invited: members.data?.data?.invited ?? [],
    viewerUserId,
    viewerPoolRole,
    searchQuery,
    nowMs: Date.now(),
  });

  const isEmptyPool =
    !model.hasSearchMiss &&
    model.memberCount === 0 &&
    model.requestCount === 0 &&
    model.invitedCount === 0;

  const targetUserId = readMemberActionsTarget(search);
  const target =
    targetUserId === null
      ? null
      : (model.members.find((row) => row.userId === targetUserId) ?? null);

  // `leavePool` is only ever offered on the viewer's own row, so its pending
  // flag needs no member check. `block` is offered on any other member's row,
  // so it goes busy only on the row whose request is actually open. The five
  // member mutations are recorded by id as they are sent, because four of them
  // share no single hook and none of these endpoints takes a request id, so the
  // id in flight is the only thing that can name the busy row.
  const busyActionId: typeMemberMenuActionId | null = leavePool.isPending
    ? "leavePool"
    : isBlockPending && target !== null && target.userId === blockingUserId
      ? "block"
      : isOpeningChat && target !== null && target.userId === openingChatUserId
        ? "message"
        : pendingMemberActionId;

  const giveAwardMemberId =
    search.sheet === GIVE_AWARD_SHEET_NAME &&
    typeof search.memberId === "string" &&
    search.memberId !== ""
      ? search.memberId
      : null;
  const giveTarget =
    giveAwardMemberId === null
      ? null
      : (model.members.find((row) => row.userId === giveAwardMemberId) ?? null);

  const reviveMemberId =
    search.sheet === REVIVE_SHEET_NAME &&
    typeof search.memberId === "string" &&
    search.memberId !== ""
      ? search.memberId
      : null;
  const reviveTarget =
    reviveMemberId === null
      ? null
      : (model.members.find((row) => row.userId === reviveMemberId) ?? null);

  // The viewer's own id resolves to nothing: `/account/report` refuses a
  // self-report, so the client never offers what the server refuses.
  const reportMemberId = readReportMemberTarget(search);
  const reportTarget =
    reportMemberId === null || reportMemberId === viewerUserId
      ? null
      : (model.members.find((row) => row.userId === reportMemberId) ?? null);

  const menuItems =
    target === null
      ? []
      : buildMemberOverflowMenu({
          viewerUserId,
          viewerPoolRole,
          targetUserId: target.userId,
          targetPoolRole: target.poolRole,
          targetStatus: target.status,
          targetContestStatus: target.contestStatus,
        });

  function handleApprove(userId: string): void {
    setBusyUserId(userId);
    approve.mutate(
      { poolId, targetUserId: userId },
      {
        onSuccess: (result) => {
          setBusyUserId(null);
          if (result.error) showApiError(result);
        },
        onError: () => {
          setBusyUserId(null);
        },
      },
    );
  }

  function handleDecline(userId: string): void {
    setBusyUserId(userId);
    reject.mutate(
      { poolId, targetUserId: userId },
      {
        onSuccess: (result) => {
          setBusyUserId(null);
          if (result.error) showApiError(result);
        },
        onError: () => {
          setBusyUserId(null);
        },
      },
    );
  }

  function handleCancelInvite(poolInviteId: string): void {
    setBusyInviteId(poolInviteId);
    revoke.mutate(
      { poolId, poolInviteId },
      {
        onSuccess: (result) => {
          setBusyInviteId(null);
          if (result.error) {
            showApiError(result);
          } else {
            void members.refetch();
          }
        },
        onError: () => {
          setBusyInviteId(null);
        },
      },
    );
  }

  function handleApproveAll(): void {
    for (const request of model.requests) {
      approve.mutate(
        { poolId, targetUserId: request.userId },
        {
          onSuccess: (result) => {
            if (result.error) showApiError(result);
          },
        },
      );
    }
    overlay.close({ kind: "dialog" });
  }

  /**
   * R4. A Commissioner cannot simply leave: the pool would have nobody running
   * it, and `/pool/leave` refuses them with a CONFLICT. They pick a successor
   * first and the picker performs the leave once the transfer lands, so the
   * plain confirm below is never raised for them.
   */
  function handleLeavePoolSelected(): void {
    if (viewerPoolRole === "owner") {
      overlay.open({ kind: "dialog", name: HAND_OVER_DIALOG_NAME });
      return;
    }
    void handleLeaveThisPool();
  }

  function goToPoolsList(): void {
    void navigate({
      to: "/pools",
      search: {
        poolId: undefined,
        q: undefined,
        sheet: undefined,
        dialog: undefined,
      },
    });
  }

  async function handleLeaveThisPool(): Promise<void> {
    // The same confirm the five role actions use, so the sentence a fan reads
    // when they leave is decided in one place and matches `apps/mobile`
    // (ISSUE-817 R3). `leavePool` interpolates no name -- the person leaving
    // is the reader -- so the empty display name is deliberate.
    const confirm = memberActionConfirm({
      actionId: "leavePool",
      targetDisplayName: "",
    });
    if (confirm === null || !(await confirmDestructive(confirm))) return;

    try {
      const result = await leavePool.mutateAsync({ poolId });
      if (showApiError(result)) return;
      goToPoolsList();
    } catch {
      showOfflineError();
    }
  }

  /**
   * The five role and membership mutations (ISSUE-817 R1-R3).
   *
   * The confirm is awaited BEFORE anything is sent, so a canceled confirm
   * sends nothing. The client also never offers an action the server would
   * refuse: `buildMemberOverflowMenu` has already decided what this viewer may
   * do to this target, and the Commissioner is not a target of any of these
   * five from anywhere (R4). That guard is not repeated here, because a second
   * copy of a permission table is a second place for it to be wrong.
   */
  async function handleMemberAction(
    actionId: typeMemberMutationActionId,
    member: { userId: string; displayName: string },
  ): Promise<void> {
    if (pendingMemberActionId !== null) return;

    const confirm = memberActionConfirm({
      actionId,
      targetDisplayName: member.displayName,
    });
    if (confirm !== null && !(await confirmDestructive(confirm))) return;

    setPendingMemberActionId(actionId);
    try {
      const result = await (actionId === "makeAdmin"
        ? setRole.mutateAsync({
            poolId,
            targetUserId: member.userId,
            poolRole: "admin",
          })
        : actionId === "makeMember"
          ? setRole.mutateAsync({
              poolId,
              targetUserId: member.userId,
              poolRole: "member",
            })
          : actionId === "suspend"
            ? suspend.mutateAsync({ poolId, targetUserId: member.userId })
            : actionId === "unsuspend"
              ? unsuspend.mutateAsync({ poolId, targetUserId: member.userId })
              : removeMember.mutateAsync({
                  poolId,
                  targetUserId: member.userId,
                }));
      if (showApiError(result)) return;
      // The generated hook invalidates `/member/list` itself, so the row
      // behind the dialog refreshes with no hand-written cache update here.
      overlay.close({ kind: "sheet", clearParams: ["memberId"] });
    } catch {
      showOfflineError();
    } finally {
      setPendingMemberActionId(null);
    }
  }

  async function handleBlockMember(payload: {
    accountId: string;
    displayName: string;
  }): Promise<void> {
    setBlockingUserId(payload.accountId);
    try {
      const outcome = await blockFan(payload);
      if (outcome.didBlock) {
        overlay.close({ kind: "sheet", clearParams: ["memberId"] });
      }
    } finally {
      setBlockingUserId(null);
    }
  }

  // Never closes the member sheet itself: success navigates away from
  // `/pools`, and a failure must leave the menu open behind the error modal.
  async function handleMessageMember(userId: string): Promise<void> {
    if (isOpeningChat) return;
    setOpeningChatUserId(userId);
    try {
      await openPrivateChat(userId);
    } finally {
      setOpeningChatUserId(null);
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pool-members-pane">
      {phase === "loading" ? (
        <div data-testid="pool-members-loading">
          <PoolMemberRowSkeleton />
        </div>
      ) : null}
      {phase === "failed" ? (
        <div data-testid="pool-members-failed">
          <InlineBanner
            variant="warning"
            message={MEMBERS_FAILED_MESSAGE}
            action={{
              label: MEMBERS_RETRY_LABEL,
              onAction: () => {
                // All THREE reads, because any one of them can be what put the
                // pane in this state. `viewer` is the newest of the three and
                // the easiest to forget: the unknown-viewer-id branch above
                // fails on `/profile/get` alone, and a retry that refetched
                // only the other two would leave the fan pressing a button
                // that cannot fix what is broken.
                void pool.refetch();
                void members.refetch();
                void viewer.refetch();
              },
            }}
          />
        </div>
      ) : null}
      {phase === "noAccess" ? (
        <div data-testid="pool-members-no-access">
          <p className="text-body text-ink">{POOL_NO_ACCESS_TITLE}</p>
          <p className="text-body text-ink-secondary">{POOL_NO_ACCESS_BODY}</p>
          {onGoBack === undefined ? null : (
            <Button variant="secondary" size="md" onClick={onGoBack}>
              {POOL_NO_ACCESS_ACTION_LABEL}
            </Button>
          )}
        </div>
      ) : null}
      {phase === "ready" ? (
        <>
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="pool-members-header"
          >
            {onGoBack === undefined ? null : (
              <Button variant="link" size="md" onClick={onGoBack}>
                {MEMBERS_BACK_LABEL}
              </Button>
            )}
            <h2 className="text-h2 text-ink">{MEMBERS_TITLE}</h2>
            {canInvite ? (
              <Button
                variant="secondary"
                size="md"
                className="ml-auto"
                data-testid="pool-members-invite"
                onClick={() => {
                  overlay.open({ kind: "sheet", name: INVITE_SHEET_NAME });
                }}
              >
                {INVITE_ENTRY_LABEL}
              </Button>
            ) : null}
          </div>
          {model.contestDataUnavailable ? (
            <div data-testid="pool-members-contest-unavailable">
              <InlineBanner
                variant="neutral"
                message={MEMBERS_CONTEST_UNAVAILABLE_MESSAGE}
                action={{
                  label: MEMBERS_RETRY_LABEL,
                  onAction: () => {
                    void members.refetch();
                  },
                }}
              />
            </div>
          ) : null}
          {isEmptyPool ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="pool-members-empty"
            >
              {MEMBERS_EMPTY_BODY}
            </p>
          ) : (
            <>
              {model.showSearch ? (
                <Input
                  label={MEMBERS_SEARCH_LABEL}
                  value={searchQuery}
                  onChange={(event) => {
                    onSearchQueryChange(event.target.value);
                  }}
                />
              ) : null}
              {model.showRequests ? (
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-h3 text-ink">
                      {sectionHeading("Requests", model.requestCount)}
                    </h3>
                    {model.showApproveAll ? (
                      <Button
                        variant="secondary"
                        size="md"
                        data-testid="pool-members-approve-all"
                        onClick={() => {
                          overlay.open({
                            kind: "dialog",
                            name: APPROVE_ALL_DIALOG_NAME,
                          });
                        }}
                      >
                        {APPROVE_ALL_CONFIRM_LABEL}
                      </Button>
                    ) : null}
                  </div>
                  <ul data-testid="pool-members-requests">
                    {model.requests.map((row) => (
                      <PoolMemberRequestRow
                        key={row.userId}
                        row={row}
                        isBusy={busyUserId === row.userId}
                        onApprove={handleApprove}
                        onDecline={handleDecline}
                      />
                    ))}
                  </ul>
                </div>
              ) : null}
              <div>
                <h3 className="text-h3 text-ink">
                  {sectionHeading("Members", model.memberCount)}
                </h3>
                {model.hasSearchMiss ? (
                  <p className="text-body text-ink-secondary">
                    {MEMBERS_SEARCH_MISS_BODY}
                  </p>
                ) : (
                  <ul data-testid="pool-members-list">
                    {model.members.map((row) => (
                      <PoolMemberRow
                        key={row.userId}
                        row={row}
                        onOpenActions={(userId) => {
                          overlay.open({
                            kind: "sheet",
                            name: MEMBER_ACTIONS_SHEET_NAME,
                            params: { memberId: userId },
                          });
                        }}
                        onOpenDetail={onOpenMemberDetail}
                      />
                    ))}
                  </ul>
                )}
              </div>
              {model.showInvited ? (
                <div>
                  <h3 className="text-h3 text-ink">
                    {sectionHeading("Invited", model.invitedCount)}
                  </h3>
                  <ul data-testid="pool-members-invited">
                    {model.invited.map((row) => (
                      <PoolMemberInvitedRow
                        key={row.poolInviteId}
                        row={row}
                        isBusy={busyInviteId === row.poolInviteId}
                        onCancel={handleCancelInvite}
                      />
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </>
      ) : null}
      {/*
        R4. Opened only from the Commissioner's own `Leave pool` row, and
        `thenLeave` is what makes this entry point transfer AND leave, unlike
        the Settings Danger zone's copy of the same dialog.

        `viewerPoolRole === "owner"` is part of `open` rather than only part of
        the row that opens it, because this overlay is URL state: without it a
        plain member could reach the picker by typing `?dialog=hand-over` and
        press a name, and `/ownership/transfer` is owner-only. The client never
        offers an action pool-api will refuse.
      */}
      <HandOverPoolDialog
        open={
          viewerPoolRole === "owner" &&
          overlay.isOpen("dialog", HAND_OVER_DIALOG_NAME)
        }
        poolId={poolId}
        thenLeave
        onOpenChange={(next) => {
          if (!next) overlay.close({ kind: "dialog" });
        }}
        onDone={goToPoolsList}
      />
      <ApproveAllDialog
        open={overlay.isOpen("dialog", APPROVE_ALL_DIALOG_NAME)}
        count={model.requestCount}
        isBusy={approve.isPending}
        onOpenChange={(next) => {
          if (!next) overlay.close({ kind: "dialog" });
        }}
        onConfirm={handleApproveAll}
      />
      <MemberActionsDialog
        open={
          overlay.isOpen("sheet", MEMBER_ACTIONS_SHEET_NAME) && target !== null
        }
        memberName={target?.displayName ?? ""}
        items={menuItems}
        readyActionIds={READY_MEMBER_ACTION_IDS}
        busyActionId={busyActionId}
        onOpenChange={(next) => {
          if (!next) {
            overlay.close({ kind: "sheet", clearParams: ["memberId"] });
          }
        }}
        onSelect={(id) => {
          if (id === "viewProfile") {
            onOpenMemberDetail(target?.userId ?? "");
            return;
          }
          if (id === "message") {
            if (target !== null) void handleMessageMember(target.userId);
            return;
          }
          if (id === "revive") {
            overlay.open({
              kind: "sheet",
              name: REVIVE_SHEET_NAME,
              params: {
                memberId: target?.userId ?? "",
                entryId: "",
              },
            });
            return;
          }
          if (id === "giveAward") {
            overlay.open({
              kind: "sheet",
              name: GIVE_AWARD_SHEET_NAME,
              params: { memberId: target?.userId ?? "" },
            });
            return;
          }
          if (id === "block") {
            void handleBlockMember({
              accountId: target?.userId ?? "",
              displayName: target?.displayName ?? "",
            });
            return;
          }
          if (id === "report") {
            overlay.open({
              kind: "sheet",
              name: REPORT_MEMBER_SHEET_NAME,
              params: { memberId: target?.userId ?? "" },
            });
            return;
          }
          if (id === "leavePool") {
            handleLeavePoolSelected();
            return;
          }
          if (isMemberMutationActionId(id) && target !== null) {
            void handleMemberAction(id, {
              userId: target.userId,
              displayName: target.displayName,
            });
          }
        }}
      />
      <InviteSheet
        open={isInviteSheetOpen(search)}
        poolId={poolId}
        activeTab={readInviteTab(search)}
        onTabChange={(next) => {
          void navigate({
            to: ".",
            search: (prev: unknown) => ({
              ...(prev as Record<string, unknown>),
              inviteTab: next,
            }),
            replace: true,
          });
        }}
        query={typeof search.inviteQ === "string" ? search.inviteQ : ""}
        onQueryChange={(next) => {
          void navigate({
            to: ".",
            search: (prev: unknown) => ({
              ...(prev as Record<string, unknown>),
              inviteQ: next === "" ? undefined : next,
            }),
            replace: true,
          });
        }}
        onOpenChange={(next) => {
          if (!next) {
            overlay.close({
              kind: "sheet",
              clearParams: ["inviteTab", "inviteQ"],
            });
          }
        }}
      />
      <GiveAwardSheet
        open={
          overlay.isOpen("sheet", GIVE_AWARD_SHEET_NAME) && giveTarget !== null
        }
        poolId={poolId}
        poolName={pool.data?.data?.pool.name ?? ""}
        targetUserId={giveTarget?.userId ?? ""}
        memberDisplayName={giveTarget?.displayName ?? ""}
        onOpenChange={(next) => {
          if (!next) {
            overlay.close({ kind: "sheet", clearParams: ["memberId"] });
          }
        }}
      />
      <ReviveSheetContainer
        poolId={poolId}
        memberUserId={reviveTarget?.userId ?? ""}
        memberName={reviveTarget?.displayName ?? ""}
        open={
          overlay.isOpen("sheet", REVIVE_SHEET_NAME) && reviveTarget !== null
        }
        onClose={() => {
          overlay.close({
            kind: "sheet",
            clearParams: ["memberId", "entryId"],
          });
        }}
      />
      {/* Mounted only while a report target resolves, and keyed by member, so
          a second report never opens on the first one's sent state. */}
      {reportTarget === null ? null : (
        <ReportAccountDialog
          key={reportTarget.userId}
          open
          onClose={() => {
            overlay.close({ kind: "sheet", clearParams: ["memberId"] });
          }}
          reportedAccountId={reportTarget.userId}
          displayName={reportTarget.displayName}
          chatId={null}
        />
      )}
    </div>
  );
};
